import { skipToken } from '@apollo/client/react'
import {
  Button,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { createHideableComponent } from '@react-aria/collections'
import { produce } from 'immer'
import { uniqBy } from 'lodash-es'
import type { MouseEvent, ReactElement, ReactNode } from 'react'
import {
  type ComponentProps,
  forwardRef,
  Suspense,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { useTranslation } from 'react-i18next'
import { VirtuosoGrid } from 'react-virtuoso'
import IcBaselinePushPin from '~icons/ic/baseline-push-pin'
import IcBaselineStoreIcon from '~icons/ic/baseline-store'
import { NsfwBlurForImage } from 'src/components/Common/NsfwBlurForImage'
import ModelFilter from 'src/components/GenerationModel/ModelFilter'
import {
  type ModelTypeTagColors,
  useGenerationModelColorConfig,
} from 'src/components/GenerationModel/modelTypeDisplay'
import {
  EmptyModelSearchResultsInDialog,
  ModelListContainerInDialog,
  ModelRefCountInDialog,
} from 'src/components/GenerationTask/MultiColumnGenerationForm/Common/ModelDialogComp'
import LoadingPage from 'src/components/LoadingPage'
import { MembershipIconCircled } from 'src/components/Membership/MembershipIcon'
import { useEditItems } from 'src/components/UserProfile/EditItems'
import type {
  GenerationModel,
  ListGenerationModelsQueryVariables,
  ListMyBookmarkedGenerationModelsQueryVariables,
  Media,
} from 'src/services/apollo/generated/graphql'
import {
  GenerationModelMarkType,
  GenerationModelType,
} from 'src/services/apollo/generated/graphql'
import {
  executeMarkGenerationModel,
  refetchGetGenerationModel,
  useInfiniteListGenerationModels,
  useInfiniteListMyBookmarkedGenerationModels,
} from 'src/services/apollo/generation-model'
import { usePresetModels } from 'src/services/helpers/model'
import {
  getPosSeqAndFeedForModelListQuery,
  isBookmarkOrderOptionPosSeq,
  OrderOption,
  translateOrderOptionToQueryOrderBy,
} from 'src/services/helpers/orderOptionParams'
import { useBlurNsfw } from 'src/services/helpers/preference'
import { useMe } from 'src/services/helpers/user'
import { useBaseModelIdsForMarkFilter } from 'src/stores/model'
import { cx } from 'src/utils/cx'
import { createTimeRange } from 'src/utils/dayjs'
import {
  isModelEarlyAccess,
  isModelPreviewTest,
} from 'src/utils/generationModel/modelPermissions'
import { getNodesFromConnection } from 'src/utils/graphql'
import { useBreakpoint } from 'src/utils/hooks/useBreakpoint'
import { getThumbnailUrl } from 'src/utils/media'
import {
  isModelMembershipRequired,
  isVideoModel,
  shouldModelBeBlurred,
} from 'src/utils/model'
import { track } from 'src/utils/tracker'
import { GenerationSelectableDialogContent } from '../GenerationSelectableDialog/GenerationSelectableDialog'
import { GenerationSelectableDialogContextProvider } from '../GenerationSelectableDialog/GenerationSelectableDialogContext'
import { GenerationSelectableDialogSearchWrapper } from '../GenerationSelectableDialog/GenerationSelectableDialogSearchWrapper'
import {
  getBookmarkListOrderChoices,
  getMarketListOrderChoices,
} from '../GenerationTask/MultiColumnGenerationForm/Common/ModelOrder'
import type {
  ModelFormContextStateType,
  ModelTabType,
} from '../GenerationTask/MultiColumnGenerationForm/Common/ModelSelectContext'
import {
  ModelFormContext,
  ModelFormContextProvider,
} from '../GenerationTask/MultiColumnGenerationForm/Common/ModelSelectContext'
import type { VersionConfirmHandler } from '../GenerationTask/MultiColumnGenerationForm/Common/ModelSelectPreview'
import ModelSelectPreview from '../GenerationTask/MultiColumnGenerationForm/Common/ModelSelectPreview'
import OrderSelect from '../OrderSelect'
import { Preloader } from '../Preloader/Preloader'
import { UIDialogResponsive } from '../ui/Modal'
import Text from '../ui/Text'
import { modelFiltersToModelType } from './ModelFilter.helper'
import { getModelTypeChipConfig } from './ModelTypeChip'

export default function ModelSelectDialog(props: {
  open: boolean
  onClose: () => void
  initState: ModelFormContextStateType
  onVersionSelected: VersionConfirmHandler
}) {
  const isMlg = useBreakpoint('mlg')
  return (
    <UIDialogResponsive
      open={props.open}
      onClose={props.onClose}
      className={
        isMlg
          ? ''
          : 'max-w-[1000px] max-h-[min(calc(100vh-64px),800px)] w-[calc(100vw-64px)]'
      }
      isMobile={isMlg}
      dismissable
    >
      {/* pass parent state inside children */}
      <ModelFormContextProvider defaultValue={props.initState}>
        <Suspense fallback={<Preloader />}>
          <ModelDialogContent
            onClose={props.onClose}
            onVersionSelected={props.onVersionSelected}
          />
        </Suspense>
      </ModelFormContextProvider>
    </UIDialogResponsive>
  )
}

function ModelDialogContent(props: {
  onClose: () => void
  onVersionSelected: VersionConfirmHandler
}) {
  const { beingMember } = useMe()
  const bookmarkLoadNewFnRef = useRef<() => void>(null)
  const isMobile = useBreakpoint('mlg')
  const [modelFormState, setModelFormState] = useContext(ModelFormContext)
  const { t } = useTranslation(['generation', 'action', 'market'])
  const tabVal = modelFormState.tab

  const setTabVal = useCallback((val: ModelTabType) => {
    setModelFormState(prev => ({
      ...prev,
      tab: val,
    }))
  }, [])

  const onTabChange = useCallback(
    (value: ModelTabType) => {
      track('model_switch_click', { value })

      setTabVal(value)
    },
    [tabVal],
  )

  const tabs = useMemo<
    Array<{ id: ModelTabType; label: string; content: ReactNode }>
  >(() => {
    return [
      {
        id: 'preset' as ModelTabType,
        label: t('generation:model.type.preset'),
        content: <PresetModelGrid type="preset" setTabVal={setTabVal} />,
      },
      {
        id: 'market' as ModelTabType,
        label: t('generation:model.type.market'),
        content: <MarketModelGrid />,
      },
      {
        id: 'bookmark' as ModelTabType,
        label: t('generation:model.type.bookmark'),
        content: (
          <BookmarkModelGrid setTabVal={setTabVal} ref={bookmarkLoadNewFnRef} />
        ),
      },
    ]
  }, [])

  return (
    <GenerationSelectableDialogContextProvider>
      <GenerationSelectableDialogContent
        onClose={props.onClose}
        title={t('generation:model.list.title')}
        showModalSheet={isMobile}
        tabVal={tabVal}
        tabs={tabs}
        onTabChange={onTabChange}
        tabsSuffix={<TabFilters />}
        onOpenDetailChange={status =>
          setModelFormState(prev => ({
            ...prev,
            openDetail: status,
          }))
        }
        openDetail={modelFormState.openDetail}
        preview={
          <ModelSelectPreview
            className={cx(
              'w-full h-[calc(var(--list-height)-68px)] mlg:h-[calc(var(--list-height)+56px)]',
              'mlg:w-72 mlg:border-l mlg:border-background-light',
              isMobile && !modelFormState.openDetail && 'hidden',
            )}
            key={modelFormState.modelId}
            onClose={props.onClose}
            onConfirm={props.onVersionSelected}
            showPinToTop={beingMember && tabVal === 'bookmark'}
            mutateAfterPin={bookmarkLoadNewFnRef.current ?? undefined}
          />
        }
        searchInput={<ModelSearchWrapper tabVal={tabVal} />}
        action={
          <Button
            color="primary"
            href="/market"
            target="_blank"
            size="large"
            startIcon={<IcBaselineStoreIcon />}
            onClick={() => {
              track('browse_mkt_click', {
                location: 'more-model',
              })
            }}
          >
            {t('generation:model.list.market')}
          </Button>
        }
      />
    </GenerationSelectableDialogContextProvider>
  )
}

// Workaround for the issue of RAC rendering a Collection within another Collection
// see: https://github.com/adobe/react-spectrum/issues/9011
const OrderSelectHideable = createHideableComponent(
  (props: ComponentProps<typeof OrderSelect>) => <OrderSelect {...props} />,
)
const ModelFilterHideable = createHideableComponent(
  (props: ComponentProps<typeof ModelFilter>) => <ModelFilter {...props} />,
)

function TabFilters() {
  const { t } = useTranslation(['common'])

  const marketListOrderChoices = getMarketListOrderChoices(t)
  const bookmarkListOrderChoices = getBookmarkListOrderChoices(t)

  const [modelFormState, setModelFormState] = useContext(ModelFormContext)
  const tabVal = modelFormState.tab
  const _baseModelIds = useBaseModelIdsForMarkFilter()
  const baseModelIds = useMemo(
    () => _baseModelIds.map(({ value }) => value),
    [_baseModelIds],
  )

  return (
    <div className="flex items-center gap-2">
      {tabVal !== 'preset' && (
        <OrderSelectHideable
          choices={
            tabVal === 'market'
              ? marketListOrderChoices
              : bookmarkListOrderChoices
          }
          onChange={v => {
            track('order_click', {
              value: v,
              scene: 'market',
            })
            if (tabVal === 'market') {
              setModelFormState(prev => ({
                ...prev,
                marketOrder: v,
              }))
            } else {
              setModelFormState(prev => ({
                ...prev,
                bookmarkListOrder: v,
              }))
            }
          }}
          value={
            tabVal === 'market'
              ? modelFormState.marketOrder
              : modelFormState.bookmarkListOrder
          }
          alwaysCollapse
        />
      )}
      {tabVal === 'market' && (
        <ModelFilterHideable
          value={{
            ...modelFormState.marketFilters,
          }}
          onChange={value => {
            setModelFormState(
              produce(state => {
                state.marketFilters = value
              }),
            )
          }}
          disabled={{
            type: true,
            baseModelId: baseModelIds,
            source: true,
            category: true,
          }}
          hideDisabled
          className="h-10"
        />
      )}
    </div>
  )
}

function ModelSearchWrapper({
  tabVal,
  ...props
}: { tabVal: string } & StyleProps) {
  const { t } = useTranslation(['generation'])
  const [{ searchKeyword }, setModelFormState] = useContext(ModelFormContext)

  const onChange = (v: string) => {
    v = v.trim()
    if (v) {
      track('panel_model_search', {
        fromPath: 'model',
        location: tabVal,
        value: v,
      })
    }
    setModelFormState(prev => ({ ...prev, searchKeyword: v }))
  }

  return (
    <GenerationSelectableDialogSearchWrapper
      {...props}
      tabVal={tabVal}
      onInputChange={onChange}
      className={cx('flex-auto', props.className)}
      onClear={() => {
        // reset immediately on clear
        setModelFormState(prev => ({
          ...prev,
          searchKeyword: '',
        }))
      }}
      searchKeyword={searchKeyword}
      placeholder={t('generation:model.list.search-placeholder')}
    />
  )
}

const BookmarkModelGrid = forwardRef<
  () => void,
  {
    setTabVal: (tabVal: ModelTabType) => void
  }
>(function BookmarkModelGrid(props, ref) {
  const { t } = useTranslation(['generation'])
  const [modelFormState, setModelFormState] = useContext(ModelFormContext)
  const { searchKeyword, bookmarkListOrder } = modelFormState
  const { beingMember, me: userInfo } = useMe()

  const isPosSeq = isBookmarkOrderOptionPosSeq(bookmarkListOrder)
  const initialPageSize = 30

  const parseArgs = useMemo(() => {
    /** Don't start fetching unless userInfo loaded */
    if (!userInfo?.id) return skipToken

    const pagingArgs = isPosSeq
      ? { first: initialPageSize }
      : { last: initialPageSize }

    const args = {
      modelType: GenerationModelType.AnyModel,
      keyword: searchKeyword || undefined,
      ...pagingArgs,
    } satisfies ListMyBookmarkedGenerationModelsQueryVariables

    return args
  }, [searchKeyword, initialPageSize, userInfo?.id, isPosSeq])

  const {
    data,
    loading: modelsLoading,
    fetchMore,
    loadingMore,
    refetch,
  } = useInfiniteListMyBookmarkedGenerationModels(parseArgs)

  const models = useMemo(() => {
    if (!data) return []
    const nodes = getNodesFromConnection(data)
    const positionedNodes = isPosSeq ? nodes : nodes.reverse()
    const filtered = uniqBy(positionedNodes, 'id').filter(
      model => !isVideoModel(model.type),
    )
    return filtered
  }, [data, isPosSeq])

  const totalCount = useMemo(() => {
    return data?.totalCount
  }, [data?.totalCount])

  const hasNext = useMemo(() => {
    if (isPosSeq) {
      return data?.pageInfo?.hasNextPage
    }
    return data?.pageInfo?.hasPreviousPage
  }, [data?.pageInfo, isPosSeq])

  const loadingMoreModels = loadingMore || modelsLoading

  const loadMore = useCallback(() => {
    if (!loadingMoreModels && hasNext) {
      const variables = (
        isPosSeq
          ? {
              after: data?.pageInfo.endCursor,
              first: initialPageSize,
            }
          : {
              before: data?.pageInfo.startCursor,
              last: initialPageSize,
            }
      ) as Partial<ListMyBookmarkedGenerationModelsQueryVariables>

      fetchMore({ variables })
    }
  }, [hasNext, loadingMoreModels, fetchMore, data, isPosSeq, initialPageSize])

  useImperativeHandle(ref, () => {
    return () => refetch()
  }, [refetch])

  const FooterComp = useCallback(
    () =>
      hasNext ? (
        <Preloader className="!my-1" />
      ) : modelFormState.searchKeyword.length > 0 ? (
        <EmptyModelSearchResultsInDialog
          scene="model"
          className="pb-20"
          actionType="search"
          onGotoClick={() => props.setTabVal('market')}
        />
      ) : null,
    [hasNext, modelFormState.searchKeyword],
  )

  const editState = useEditItems({
    size: 'small',
    actionButtonName: t('generation:model.manage-bookmarks.label'),
    deleteItem: async modelId => {
      await executeMarkGenerationModel({
        modelId,
        target: false,
        type: GenerationModelMarkType.Bookmark,
      })
      refetchGetGenerationModel(modelId)
    },
    beforeDelete: (ids: string[]) => {
      track('delete_bookmark_click', {
        type: 'model',
        number: ids.length,
      })
    },
    afterDelete: refetch,
    dialogTitle: t('generation:model.manage-bookmarks.confirm-title'),
    dialogContent: t('generation:model.manage-bookmarks.confirm-content'),
    onActionButtonClick: () => {
      track('manage_bookmark_click', { type: 'model' })
    },
  })

  if (models.length === 0) {
    if (modelsLoading)
      return (
        <div className="w-full min-h-[360px] h-[var(--list-height)]">
          <LoadingPage fullHeight />
        </div>
      )

    return (
      <EmptyModelSearchResultsInDialog
        actionType="search"
        scene="model"
        className="h-[var(--list-height)]"
        onGotoClick={() => props.setTabVal('market')}
      />
    )
  }

  const pinAvailable =
    beingMember && modelFormState.bookmarkListOrder === OrderOption.newest

  return (
    <RadioGroup
      value={modelFormState.modelId}
      className="relative flex-nowrap"
      // ensure grid is scrollable
      style={{
        height: 'var(--list-height)',
      }}
    >
      <div
        className={cx(
          'z-20 sticky top-0 left-0 right-0 px-1 py-3',
          'mlg:absolute mlg:top-auto mlg:bottom-0',
        )}
      >
        {editState.editItemsAction}
      </div>

      <VirtuosoGrid
        className="-mt-2 mlg:mt-0 mlg:mb-14 w-full min-h-[140px]"
        totalCount={totalCount}
        data={models}
        endReached={loadMore}
        itemContent={(idx, model) => {
          const {
            title,
            type,
            media,
            id,
            refCount,
            isPrivate,
            curations,
            latestAvailableVersion,
          } = model
          const pinned = pinAvailable && idx <= 5
          return (
            <ModelControlLabel
              key={id}
              control={
                editState.editing ? (
                  <Checkbox
                    className={cx(
                      'z-10 absolute top-2 right-2 p-0 rounded text-primary',
                      'bg-background translate-x-1/2 -translate-y-1/2',
                    )}
                    checked={editState.selectedItems.includes(id)}
                    onChange={() => {
                      editState.toggleSelected(id)
                    }}
                    sx={{ borderRadius: '3px' }}
                  />
                ) : (
                  <Radio
                    className="sr-only"
                    onChange={() => {
                      setModelFormState(prev => ({
                        ...prev,
                        type: 'bookmark',
                        versionId: latestAvailableVersion?.id || null,
                        modelId: id,
                        name: title,
                        openDetail: true,
                      }))
                    }}
                  />
                )
              }
              selected={!editState.editing && modelFormState.modelId === id}
              modelId={id}
              modelType={type}
              modelCurations={curations}
              title={title}
              media={media}
              badge={<ModelCornerBadge model={model} />}
              showRefCount={!isPrivate}
              refCount={refCount}
              onClick={() => {
                if (editState.editing || modelFormState.modelId !== id) return
                setModelFormState(prev => ({
                  ...prev,
                  type: 'bookmark',
                  openDetail: true,
                }))
              }}
              extraComp={
                <ModelPinnedIcon
                  className="absolute right-0 top-0 translate-y-[3px] -translate-x-[3px]"
                  pinned={pinned}
                />
              }
            />
          )
        }}
        overscan={200}
        components={{
          Footer: FooterComp,
          List: ModelListContainerInDialog,
        }}
      />
    </RadioGroup>
  )
})

function PresetModelGrid(props: {
  type: ModelTabType
  setTabVal: (tabVal: ModelTabType) => void
}) {
  const [modelFormState, setModelFormState] = useContext(ModelFormContext)
  const [selectedState] = useContext(ModelFormContext)

  const { modelList: presetModelList, loading: presetModelListLoading } =
    usePresetModels()

  // TODO: remove filter after backend implemented authorId + keyword filtering
  const filteredModels = useMemo(
    () =>
      presetModelList.filter(m =>
        (m.title ?? '')
          .toLowerCase()
          .includes(modelFormState.searchKeyword.toLowerCase()),
      ),
    [presetModelList, modelFormState.searchKeyword],
  )

  /*
   * Render
   */

  if (filteredModels.length === 0) {
    if (presetModelListLoading) {
      return (
        <div className="w-full min-h-[360px] h-[var(--list-height)]">
          <LoadingPage fullHeight />
        </div>
      )
    } else {
      return (
        <EmptyModelSearchResultsInDialog
          actionType="search"
          scene="model"
          className="h-[var(--list-height)]"
          onGotoClick={() => props.setTabVal('market')}
        />
      )
    }
  } else {
    return (
      <>
        <RadioGroup
          value={selectedState.modelId}
          // ensure grid is scrollable
          style={{
            height: 'var(--list-height)',
          }}
          // Adding 'flex-nowrap' resolves a scroll jump issue that occurs specifically:
          // - On Chrome browsers
          // - When scrolling slowly
          // - When the cursor is hovering over the RadioGroup
          // Do not remove unless you have verified the scroll behavior remains correct.
          className="flex-nowrap"
        >
          <ModelListContainerInDialog>
            {filteredModels.map(model => {
              const {
                title,
                type,
                curations,
                media,
                id,
                refCount,
                latestAvailableVersion,
              } = model
              return (
                <ModelControlLabel
                  key={id}
                  control={
                    <Radio
                      className="sr-only"
                      onChange={() => {
                        setModelFormState(prev => ({
                          ...prev,
                          type: 'preset',
                          versionId: latestAvailableVersion?.id || null,
                          modelId: id,
                          name: title,
                          openDetail: true,
                        }))
                      }}
                    />
                  }
                  selected={modelFormState.modelId === id}
                  modelId={id}
                  modelType={type}
                  modelCurations={curations}
                  title={title}
                  media={media}
                  badge={<ModelCornerBadge model={model} />}
                  showRefCount
                  refCount={refCount}
                  onClick={() => {
                    if (modelFormState.modelId !== id) return
                    setModelFormState(prev => ({
                      ...prev,
                      type: 'preset',
                      openDetail: true,
                    }))
                  }}
                />
              )
            })}
          </ModelListContainerInDialog>
          {modelFormState.searchKeyword.length > 0 && (
            <EmptyModelSearchResultsInDialog
              actionType="search"
              scene="model"
              onGotoClick={() => props.setTabVal('market')}
            />
          )}
        </RadioGroup>
      </>
    )
  }
}

function MarketModelGrid() {
  const [{ marketOrder }] = useContext(ModelFormContext)
  const [modelFormState, setModelFormState] = useContext(ModelFormContext)

  const [blurNsfw] = useBlurNsfw()

  const initialPageSize = 36

  const { posSeq: isPosSeq, feed } = getPosSeqAndFeedForModelListQuery({
    order: marketOrder,
    keyword: modelFormState.searchKeyword,
  })

  const parseArgs = useMemo(() => {
    const pagingArgs = isPosSeq
      ? { first: initialPageSize }
      : { last: initialPageSize }
    const marketFilters = modelFormState.marketFilters
    const filterTypes = marketFilters && modelFiltersToModelType(marketFilters)

    const args = {
      ...(Array.isArray(filterTypes?.type)
        ? { types: filterTypes.type }
        : { type: GenerationModelType.AnyModel }),

      timeRange: marketFilters?.time
        ? createTimeRange(marketFilters.time)
        : undefined,

      feed,
      orderBy: translateOrderOptionToQueryOrderBy(marketOrder),
      keyword: modelFormState.searchKeyword || undefined,
      ...pagingArgs,
    } satisfies ListGenerationModelsQueryVariables

    return args
  }, [
    feed,
    isPosSeq,
    marketOrder,
    modelFormState.marketFilters,
    modelFormState.searchKeyword,
    initialPageSize,
  ])
  const {
    data,
    loading: modelsLoading,
    fetchMore,
    loadingMore,
  } = useInfiniteListGenerationModels(parseArgs, {
    // Prevent market feeds from refetching
    // The data will be different each fetch
    fetchPolicy: 'cache-first',
    context: {
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  })

  const models = useMemo(() => {
    if (!data) return []
    const nodes = getNodesFromConnection(data)
    const positionedNodes = isPosSeq ? nodes : nodes.reverse()
    const filtered = uniqBy(positionedNodes, 'id')
    return filtered
  }, [data, isPosSeq])

  const totalCount = useMemo(() => {
    return data?.totalCount
  }, [data?.totalCount])

  const hasNext = useMemo(() => {
    if (isPosSeq) {
      return data?.pageInfo?.hasNextPage
    }
    return data?.pageInfo?.hasPreviousPage
  }, [data?.pageInfo, isPosSeq])

  const loadingMoreModels = loadingMore || modelsLoading

  const loadMore = useCallback(() => {
    if (!loadingMoreModels && hasNext) {
      const variables = (
        isPosSeq
          ? {
              after: data?.pageInfo.endCursor,
              first: initialPageSize,
            }
          : {
              before: data?.pageInfo.startCursor,
              last: initialPageSize,
            }
      ) as Partial<ListGenerationModelsQueryVariables>

      fetchMore({ variables })
    }
  }, [hasNext, loadingMoreModels, fetchMore, data, isPosSeq, initialPageSize])

  /*
   * Render
   */

  const FooterComp = useCallback(
    () =>
      // TODO: Replace with loadingMoreModels instead once the loading states are correct
      hasNext ? (
        <Preloader className="!my-1" />
      ) : modelFormState.searchKeyword.length > 0 ? (
        <EmptyModelSearchResultsInDialog
          actionType="go"
          scene="model"
          className="pb-20"
        />
      ) : null,
    [hasNext, modelFormState.searchKeyword],
  )

  if (models.length === 0) {
    if (modelsLoading) {
      return (
        <div className="w-full min-h-[360px] h-[var(--list-height)]">
          <LoadingPage fullHeight />
        </div>
      )
    } else {
      return (
        <EmptyModelSearchResultsInDialog
          actionType="go"
          scene="model"
          className="h-[var(--list-height)]"
          showDescription
        />
      )
    }
  } else {
    return (
      <RadioGroup
        value={modelFormState.modelId}
        // ensure grid is scrollable
        style={{
          height: 'var(--list-height)',
        }}
      >
        <VirtuosoGrid
          className="w-full min-h-[140px]"
          totalCount={totalCount}
          data={models}
          endReached={loadMore}
          itemContent={(idx, model) => {
            const {
              title,
              type,
              curations,
              media,
              id,
              refCount,
              latestAvailableVersion,
            } = model
            return (
              <ModelControlLabel
                key={id}
                control={
                  <Radio
                    className="sr-only"
                    onChange={() => {
                      setModelFormState(prev => ({
                        ...prev,
                        type: 'market',
                        versionId: latestAvailableVersion?.id || null,
                        modelId: id,
                        name: title,
                        openDetail: true,
                      }))
                    }}
                  />
                }
                selected={modelFormState.modelId === id}
                modelId={id}
                modelType={type}
                modelCurations={curations}
                title={title}
                media={media}
                badge={<ModelCornerBadge model={model} />}
                blurCover={shouldModelBeBlurred(model) && blurNsfw}
                showRefCount
                refCount={refCount}
                onClick={() => {
                  if (modelFormState.modelId !== id) return
                  setModelFormState(prev => ({
                    ...prev,
                    type: 'market',
                    openDetail: true,
                  }))
                }}
              />
            )
          }}
          overscan={200}
          components={{
            Footer: FooterComp,
            List: ModelListContainerInDialog,
          }}
        />
      </RadioGroup>
    )
  }
}

type LabelDisplayProps = {
  height?: number
  width?: number

  blurCover?: boolean
  showRefCount?: boolean
  refCount?: number
}

export function ModelControlLabel(
  props: {
    control: ReactElement
    selected: boolean
    disabled?: boolean

    modelId: string
    modelType: GenerationModelType
    modelCurations?: string[]
    title: string
    preview?: string
    media?: Media
    badge?: ReactNode

    titleClassName?: string

    onClick?: () => void

    extraComp?: JSX.Element
  } & LabelDisplayProps,
) {
  const {
    control,
    selected,
    disabled,

    modelId,
    modelType,
    modelCurations,
    title,
    media,
    badge,
    preview = getThumbnailUrl(media),
    titleClassName,

    blurCover,
    showRefCount,
    refCount,

    onClick,

    extraComp,
  } = props

  const colorConfig = useGenerationModelColorConfig()

  const onLinkClick = useCallback((e: MouseEvent) => {
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return
    e.preventDefault()
    e.currentTarget.parentElement?.click()
  }, [])

  /**
   * Render
   */

  if (!modelId) {
    return (
      <div className="aspect-square p-3 grid place-content-center text-center text-primary-light border border-background-light rounded-xl">
        Model Deleted
      </div>
    )
  }

  return (
    <FormControlLabel
      // h-fit to make safari happy for items in grid
      className="group/label relative m-0 block isolate h-fit"
      disableTypography
      value={modelId}
      disabled={disabled}
      title={title}
      control={control}
      onClick={onClick}
      label={
        <a
          className="flex flex-col items-center"
          tabIndex={-1}
          href={`/model/${modelId}`}
          target="_blank"
          onClick={onLinkClick}
        >
          <div
            className={cx(
              'w-full aspect-square relative rounded-xl m-[3px] overflow-hidden',
              'ring-2 ring-offset-1 ring-offset-background-light',
              selected
                ? 'ring-purple-400 dark:ring-purple-600'
                : 'ring-transparent',
              'group-[&:has(:focus-visible)]/label:ring-purple-600',
              'dark:group-[&:has(:focus-visible)]/label:ring-purple-400',
            )}
          >
            <img
              src={preview}
              alt=""
              loading="lazy"
              decoding="async"
              className="size-full object-top object-cover"
            />
            {blurCover && <NsfwBlurForImage className="rounded-xl" />}
            <ModelPreviewCornerTypeTag
              modelType={modelType}
              modelCurations={modelCurations}
              colorConfig={colorConfig}
            />
            {badge}

            {showRefCount && refCount != null && (
              <ModelRefCountInDialog refCount={refCount} />
            )}
            {extraComp}
          </div>

          {/* height has to be hardcoded, or virtuoso list will be "jumpy" */}
          <p
            className={cx(
              // TODO: replace with h-[2lh] if widely available
              'pt-1 h-11 text-center text-xs leading-5 font-semibold',
              'line-clamp-2 break-all hyphens-auto',
              titleClassName,
            )}
          >
            {title}
          </p>
        </a>
      }
    />
  )
}

function ModelPreviewCornerTypeTag(props: {
  modelType?: GenerationModelType
  modelCurations?: string[]
  colorConfig?: ModelTypeTagColors
}) {
  const { t } = useTranslation(['market'])
  const { modelType, modelCurations, colorConfig } = props
  const modelVersionStyles = modelType
    ? getModelTypeChipConfig(
        t,
        { type: modelType, curations: modelCurations },
        colorConfig,
      )
    : undefined

  if (!modelVersionStyles) return null

  const shouldDisplayDense = modelVersionStyles.label.length > 10

  return (
    <span
      className={cx(
        modelVersionStyles?.bgClassName,
        'text-black text-xs leading-4',
        'absolute top-0 left-0 translate-x-[3px] translate-y-[3px] px-2 shadow rounded-lg',
        shouldDisplayDense && 'px-1 tracking-tighter',
      )}
      style={modelVersionStyles.bgStyle}
    >
      {modelVersionStyles?.label}
    </span>
  )
}

function ModelPinnedIcon(props: { pinned?: boolean } & StyleProps) {
  if (!props.pinned) return null

  const icon = (
    <IcBaselinePushPin className="size-3 text-white rotate-45 -translate-x-px translate-y-px" />
  )

  return (
    <span className={cx('rounded-full p-0.5 bg-purple-400', props.className)}>
      {icon}
    </span>
  )
}

export function ModelCornerBadge(props: { model: GenerationModel }) {
  if (isModelPreviewTest(props.model)) {
    return (
      <div className="bg-gradient-purple-pink absolute w-full h-4 bottom-[20%] translate-y-[20%] -right-[25%] -rotate-45 flex items-center justify-center text-xs font-semibold text-gray-50 shadow-md">
        <Text i18nKey="market:model.preview-test.label" context="corner" />
      </div>
    )
  }
  if (isModelEarlyAccess(props.model)) {
    return (
      <div className="bg-gradient-purple-pink absolute w-full h-4 bottom-[20%] translate-y-[20%] -right-[25%] -rotate-45 flex items-center justify-center text-xs font-semibold text-gray-50 shadow-md">
        <Text i18nKey="market:model.early-access.label" context="corner" />
      </div>
    )
  }

  // TODO: [MODEL_PERMISSION] Temporary solution for member-exclusive models
  if (isModelMembershipRequired(props.model.latestAvailableVersion?.id)) {
    return (
      <MembershipIconCircled className="absolute bottom-1 right-1 size-5 shadow-md" />
    )
  }

  return null
}
